import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

import { resolveFloatingPopoverPosition, type PopoverPlacement } from "../foundations/popover";
import { topmostModalEl, topmostModalMountNode } from "../Modal/modalStack";
import { tooltipBubbleSurfaceCss } from "../Tooltip/tooltipBubble";
import { isTooltipBubbleBlockingPointerTarget } from "../Tooltip/tooltipPointerTarget";
import {
  hasTooltipFocusIntent,
  isTooltipPointerOpenSuppressed,
  noteTooltipPointerBoundary,
  subscribeTooltipPointerIntent,
} from "../Tooltip/useTooltipController";
import {
  TRUNCATE_ATTR,
  TRUNCATE_LINES_ATTR,
  isTextOverflowing,
  readTruncationText,
} from "./truncation";

// 타이밍은 Tooltip 계열과 정합(새 대상마다 포인터 지연, 닫힘은 dead-zone grace).
const OPEN_DELAY_MS = 250;
const HOVER_GRACE_MS = 100;
const FADE_IN_MS = 120;
const OFFSET = 5;
const MARGIN = 8;

// 공유 단일 버블. 위치/표시만 인스턴스가 정하고 시각은 Tooltip과 공용(tooltipBubbleSurfaceCss).
// pointer-events:auto — 버블 위로 포인터를 옮겨 읽을 수 있게(WCAG 1.4.13 hoverable). 트리거→버블
// dead-zone은 grace-hide(HOVER_GRACE_MS)가, 버블 체류는 버블 mouseenter가 cancel-hide로 잇는다.
const Bubble = styled.span`
  position: fixed;
  display: flex;
  pointer-events: auto;
  ${tooltipBubbleSurfaceCss}
  opacity: 1;
  transition: opacity ${FADE_IN_MS}ms ease-in;
  @starting-style {
    opacity: 0;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export interface TruncationTooltipHostProps {
  /** 잘림 툴팁 방향. 기본 `"bottom"`. 뷰포트 가장자리에선 auto-flip. */
  placement?: PopoverPlacement;
}

type Shown = { el: HTMLElement; text: string };

/**
 * `TruncatedText`가 만든 잘린 텍스트들의 hover 툴팁을 **단일 공유 인스턴스**로 처리한다(RFC-truncation-tooltip §아키텍처).
 * document 레벨 이벤트 위임으로 잘림 요소를 찾아, 실제로 overflow 났을 때만(hover 시점 측정) 버블 하나를
 * 그 요소에 재배치해 띄운다. 요소마다 툴팁/훅을 붙이지 않아 대형 표에서도 가볍고, 요소의 CSS를 건드리지
 * 않아 truncation이 깨지지 않는다. 앱 루트에 한 번 배치한다(`ToastHost`와 동형).
 */
export function TruncationTooltipHost({ placement = "bottom" }: TruncationTooltipHostProps) {
  const [shown, setShown] = useState<Shown | null>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const [portalEl, setPortalEl] = useState<HTMLDivElement | null>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const targetRef = useRef<HTMLElement | null>(null);
  // 버블(hover bridge)이 effect 안의 hide 타이머를 제어하도록 노출. effect가 1회 채운다.
  const bridgeRef = useRef<{
    enterBubble: () => void;
    leaveBubble: () => void;
    dismissCoveredTarget: () => void;
    dismissActive: () => void;
  }>({
    enterBubble: () => {},
    leaveBubble: () => {},
    dismissCoveredTarget: () => {},
    dismissActive: () => {},
  });

  useEffect(() => {
    const unsubscribePointerIntent = subscribeTooltipPointerIntent();
    const showTimer = { id: null as ReturnType<typeof setTimeout> | null };
    const hideTimer = { id: null as ReturnType<typeof setTimeout> | null };
    let bubbleHovered = false;
    const clearShow = () => {
      if (showTimer.id !== null) clearTimeout(showTimer.id);
      showTimer.id = null;
    };
    const clearHide = () => {
      if (hideTimer.id !== null) clearTimeout(hideTimer.id);
      hideTimer.id = null;
    };

    const showNow = (el: HTMLElement) => {
      clearHide();
      clearShow();
      setShown({ el, text: readTruncationText(el) });
    };
    const scheduleShow = (el: HTMLElement) => {
      clearHide();
      clearShow();
      bubbleHovered = false;
      // 다른 행에서 떠 있던 bubble을 새 anchor로 즉시 옮기지 않는다. 이전 것은 바로 닫고 새 대상에서
      // dwell을 다시 확인해야 리스트를 훑는 동안 bubble이 위아래로 튀지 않는다.
      setShown(null);
      showTimer.id = setTimeout(() => {
        showTimer.id = null;
        setShown({ el, text: readTruncationText(el) });
      }, OPEN_DELAY_MS);
    };
    const scheduleHide = () => {
      clearShow();
      clearHide();
      hideTimer.id = setTimeout(() => {
        hideTimer.id = null;
        bubbleHovered = false;
        // grace 동안 marker로 돌아오면 onPointerOver가 이 타이머를 취소한다. 실제 hide까지
        // 완료됐다면 소유자 없는 anchor도 함께 비워, 다음 진입을 same-target dedupe가 삼키지 않게 한다.
        targetRef.current = null;
        setShown(null);
      }, HOVER_GRACE_MS);
    };
    // 마커만 찾는다(측정은 안 함 — dedup 뒤로 미뤄 intra-cell 이동 시 반복 reflow 방지).
    const findMarker = (origin: EventTarget | null): HTMLElement | null =>
      origin instanceof Element ? origin.closest<HTMLElement>(`[${TRUNCATE_ATTR}]`) : null;
    // ContentSwitcher처럼 focusable control 안에 잘리는 라벨 marker가 있는 구조도 지원한다.
    // descendant가 하나일 때만 선택해 composite control의 어느 라벨인지 임의로 추측하지 않는다.
    const findFocusMarker = (origin: EventTarget | null): HTMLElement | null => {
      const ancestor = findMarker(origin);
      if (ancestor) return ancestor;
      if (!(origin instanceof HTMLElement)) return null;
      const descendants = origin.querySelectorAll<HTMLElement>(`[${TRUNCATE_ATTR}]`);
      return descendants.length === 1 ? descendants[0] : null;
    };
    const isOverflowing = (el: HTMLElement) =>
      isTextOverflowing(el, el.hasAttribute(TRUNCATE_LINES_ATTR));
    let focusOwner: HTMLElement | null = null;
    let focusMarker: HTMLElement | null = null;
    let focusDismissed = false;
    let pointerMarker: HTMLElement | null = null;
    let focusMutationObserver: MutationObserver | null = null;

    const disconnectFocusMutationObserver = () => {
      focusMutationObserver?.disconnect();
      focusMutationObserver = null;
    };

    // focus가 유지된 채 aria-pressed/class/text가 바뀌면 font metric·내용 폭도 달라질 수 있다.
    // 앱 전체 marker를 상시 관찰하지 않고 현재 focus owner 하나만 관찰해, fit↔overflow 전이를
    // 같은 focus 수명 안에서 반영한다(ContentSwitcher fixed selected regular→bold 경계).
    const reconcileFocusedMarker = () => {
      const owner = focusOwner;
      const marker = focusMarker;
      if (
        focusDismissed ||
        !owner ||
        !marker ||
        !owner.isConnected ||
        !marker.isConnected ||
        document.activeElement !== owner ||
        !hasTooltipFocusIntent(owner)
      ) {
        return;
      }

      const trackedMarker = targetRef.current;
      // 실제 pointer가 다른 marker를 소유 중이면 그 최신 intent를 건드리지 않는다. pointer가
      // 떠날 때 scheduleHideOrRestoreFocus가 갱신된 focus marker를 복원한다.
      if (trackedMarker && trackedMarker !== marker) return;

      if (isOverflowing(marker)) {
        targetRef.current = marker;
        showNow(marker);
        return;
      }

      if (trackedMarker !== marker) return;
      clearShow();
      clearHide();
      pointerMarker = null;
      bubbleHovered = false;
      targetRef.current = null;
      setShown(null);
    };

    const observeFocusOwner = (owner: HTMLElement) => {
      disconnectFocusMutationObserver();
      focusMutationObserver = new MutationObserver(reconcileFocusedMarker);
      focusMutationObserver.observe(owner, {
        attributes: true,
        attributeFilter: ["aria-pressed", "class", "style"],
        childList: true,
        characterData: true,
        subtree: true,
      });
    };

    // focus와 pointer는 같은 버블을 공유하되 소유권은 분리한다. focus-visible owner가 유지되는
    // 동안 label→control padding 같은 pointer 경계 이동이 focus로 연 버블을 닫으면 안 된다.
    const restoreFocusMarker = () => {
      const owner = focusOwner;
      const marker = focusMarker;
      if (
        focusDismissed ||
        !owner ||
        !marker ||
        !owner.isConnected ||
        !marker.isConnected ||
        document.activeElement !== owner ||
        !hasTooltipFocusIntent(owner) ||
        !isOverflowing(marker)
      ) {
        return false;
      }
      targetRef.current = marker;
      showNow(marker);
      return true;
    };
    const scheduleHideOrRestoreFocus = () => {
      if (!restoreFocusMarker()) scheduleHide();
    };
    const dismissActive = () => {
      const trackedMarker = targetRef.current;
      const retainTrackedTarget =
        trackedMarker?.isConnected === true &&
        (pointerMarker === trackedMarker || (focusOwner !== null && focusMarker === trackedMarker));
      clearShow();
      clearHide();
      bubbleHovered = false;
      // Escape/겹침/scroll dismiss 뒤에는 같은 focus 수명에서 pointer 경계가 버블을 되살리지 않는다.
      if (focusOwner) focusDismissed = true;
      // focus가 이미 blur되고 pointer도 bubble로 넘어간 경우에는 소유자가 없다. 이때 stale
      // target을 남기면 다음 실제 marker 진입이 equality dedupe에 막힌다.
      if (!retainTrackedTarget) targetRef.current = null;
      setShown(null);
    };
    const dismissCoveredTarget = () => {
      focusDismissed = focusOwner !== null;
      pointerMarker = null;
      bubbleHovered = false;
      clearShow();
      clearHide();
      targetRef.current = null;
      setShown(null);
    };
    const enterBubble = () => {
      bubbleHovered = true;
      clearHide();
    };
    const leaveBubble = () => {
      bubbleHovered = false;
      scheduleHideOrRestoreFocus();
    };
    // 버블 hover bridge와 document Escape가 이 effect의 focus/pointer 상태 머신을 제어하도록 노출.
    bridgeRef.current = {
      enterBubble,
      leaveBubble,
      dismissCoveredTarget,
      dismissActive,
    };

    const onPointerOver = (event: PointerEvent) => {
      const el = findMarker(event.target);
      if (!el) return;
      noteTooltipPointerBoundary(event);
      if (el === targetRef.current) {
        // focus와 같은 marker여도 실제 pointer 진입은 별도 소유권이다. blur가 focus 소유만
        // 해제한 뒤에도 포인터가 머물면 현재 버블을 hover 소유로 이어간다.
        if (!isTooltipPointerOpenSuppressed()) pointerMarker = el;
        clearHide(); // 같은 요소 안 재진입 — 측정 전에 early-return. 대기 중 grace-hide 취소
        return;
      }
      // scroll이 정지한 포인터 아래 행을 바꾸며 만든 boundary pointerover는 새 hover intent가 아니다.
      // target은 기록해 같은 행 안의 후속 boundary event도 막고, 실제 pointermove 뒤 leave→enter가
      // 일어났을 때만 아래 overflow 측정/open delay를 다시 시작한다.
      if (isTooltipPointerOpenSuppressed()) {
        clearShow();
        clearHide();
        pointerMarker = null;
        bubbleHovered = false;
        targetRef.current = el;
        // scroll 뒤 keyboard focus는 독립 intent다. stationary pointer boundary가 그 버블을
        // 다시 열 수는 없지만, 이미 focus로 열린 버블까지 닫아서도 안 된다.
        if (!restoreFocusMarker()) setShown(null);
        return;
      }
      if (!isOverflowing(el)) {
        pointerMarker = null;
        return; // 안 잘렸으면 안 띄운다
      }
      pointerMarker = el;
      targetRef.current = el;
      scheduleShow(el);
    };

    // 키보드 focus로 도달한 잘림(포커스 가능한 marker 또는 marker 하나를 품은 control) —
    // RFC-truncation-tooltip §형태의 focus 트리거. focus는 의도적이라 지연 없이 즉시 표시. non-focusable
    // (표 셀)은 focusin이 안 와 무영향. 마우스 클릭은 :focus-visible로, component lifecycle
    // focus는 shared marker로 제외한다(클릭/자동 focus로 popup 여는 컨트롤 위 오발동 방지).
    const onFocusIn = (event: FocusEvent) => {
      const focused = event.target;
      if (!(focused instanceof HTMLElement) || !hasTooltipFocusIntent(focused)) return;
      const el = findFocusMarker(focused);
      if (!el) return;
      focusOwner = focused;
      focusMarker = el;
      focusDismissed = false;
      observeFocusOwner(focused);
      if (!isOverflowing(el)) return;
      // scroll-suppressed pointerover가 같은 el을 targetRef에 기록했어도 keyboard focus는 독립적인
      // 명시 intent다. target equality로 dedupe하지 않고 pending pointer open을 즉시 승격한다.
      if (targetRef.current !== el) bubbleHovered = false;
      targetRef.current = el;
      showNow(el);
    };

    // pointer leave / focus out 공통. leave가 **추적 요소에서 난 것**일 때만 닫는다 — 이 가드가 없으면
    // 전역 focusout(다른 input의 blur 등)이 pointer로 떠 있던 툴팁을 잘못 닫는다. pointerout은
    // 원래 스코프되지만 focusout은 document 전역이라 필수.
    const onLeave = (event: PointerEvent | FocusEvent) => {
      if ("clientX" in event && "clientY" in event) noteTooltipPointerBoundary(event);
      if (event.type === "focusout") {
        const owner = focusOwner;
        if (!owner || event.target !== owner) return;
        const related = event.relatedTarget;
        if (related instanceof Node && owner.contains(related)) return;
        const hoveredMarker = pointerMarker;
        const hoveredBubble = bubbleHovered;
        const trackedMarker = targetRef.current;
        disconnectFocusMutationObserver();
        focusOwner = null;
        focusMarker = null;
        focusDismissed = false;
        // focus와 pointer 소유가 겹친 채 blur되면 hover가 현재 버블을 이어받는다. 여기서
        // target을 지우면 pointer가 정지한 동안 재진입 이벤트가 없어 전체 값이 사라진다.
        if (hoveredMarker && hoveredMarker.isConnected && isOverflowing(hoveredMarker)) {
          targetRef.current = hoveredMarker;
          // pointer가 focus marker와 같은 현재 target이면 열린 버블/대기 타이머를 그대로
          // 이어간다. focus가 pointer target을 덮어쓴 경우엔 새 hover intent로 다시 연다.
          if (hoveredMarker === trackedMarker) clearHide();
          else scheduleShow(hoveredMarker);
          return;
        }
        // marker를 떠나 bubble 위에 머문 경우에도 hover bridge가 focus 소유를 이어받는다.
        // bubble은 portal에 있어 focus owner의 descendant가 아니므로 별도 소유권이 필요하다.
        if (
          hoveredBubble &&
          trackedMarker &&
          trackedMarker.isConnected &&
          isOverflowing(trackedMarker)
        ) {
          targetRef.current = trackedMarker;
          clearHide();
          return;
        }
        if (!trackedMarker) return;
        targetRef.current = null;
        scheduleHide();
        return;
      }
      const hoveredMarker = pointerMarker;
      if (hoveredMarker) {
        const from = event.target;
        const related = event.relatedTarget;
        if (
          from instanceof Node &&
          hoveredMarker.contains(from) &&
          !(related instanceof Node && hoveredMarker.contains(related))
        ) {
          pointerMarker = null;
        }
      }
      const el = targetRef.current;
      if (!el) return;
      const from = event.target;
      if (!(from instanceof Node) || !el.contains(from)) return; // 추적 요소 밖에서 난 leave는 무시
      const related = event.relatedTarget;
      if (related instanceof Node && el.contains(related)) return; // 요소 안 이동은 유지
      targetRef.current = null;
      scheduleHideOrRestoreFocus();
    };

    // scroll은 visible bubble과 pending open을 모두 취소한다. targetRef는 유지해 같은 요소 위에
    // 포인터가 계속 있는 동안 자동 재노출하지 않고, 실제 leave→enter 뒤에만 다시 intent를 잰다.
    const onScroll = () => {
      clearShow();
      clearHide();
      pointerMarker = null;
      bubbleHovered = false;
      if (focusOwner) focusDismissed = true;
      setShown(null);
    };

    document.addEventListener("pointerover", onPointerOver, true);
    document.addEventListener("pointerout", onLeave, true);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onLeave, true);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      clearShow();
      clearHide();
      document.removeEventListener("pointerover", onPointerOver, true);
      document.removeEventListener("pointerout", onLeave, true);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", onLeave, true);
      window.removeEventListener("scroll", onScroll, true);
      disconnectFocusMutationObserver();
      unsubscribePointerIntent();
    };
  }, []);

  // 영속 portal 컨테이너 — 한 번 만들어 재사용한다. target이 바뀔 때마다 재생성하면 버블이 재마운트되며
  // @starting-style fade가 재생돼 스캔 시 깜빡인다. 컨테이너가 영속이면 요소 간 전환에서 버블이
  // 계속 마운트된 채 위치만 이동한다. mount node(모달 안/밖)는 아래 효과가 조정한다.
  useEffect(() => {
    const el = document.createElement("div");
    el.setAttribute("data-c6-truncation-tooltip-root", "");
    document.body.appendChild(el);
    setPortalEl(el);
    return () => {
      setPortalEl(null);
      el.remove();
    };
  }, []);

  // 트리거가 topmost 모달 안이면 컨테이너를 그 Dialog로 재부착(모달 위·fixed 유지), 아니면 body로.
  // 경계를 넘을 때만 옮기므로 같은 컨텍스트 내 스캔에선 버블이 재마운트되지 않는다.
  useEffect(() => {
    if (!shown || !portalEl) return;
    const owner = topmostModalEl();
    const inModal = owner != null && owner.contains(shown.el);
    const mountNode = (inModal ? topmostModalMountNode() : null) ?? document.body;
    if (portalEl.parentNode !== mountNode) mountNode.appendChild(portalEl);
  }, [shown, portalEl]);

  // paint 전 배치 + resize 추종. scroll dismiss는 위의 영속 listener가 visible/pending 모두 처리한다.
  // anchor가 scroll/resize 없이 detach되면 닫는다.
  useLayoutEffect(() => {
    if (!shown || !portalEl) return;
    const update = () => {
      const bubble = bubbleRef.current;
      if (!shown.el.isConnected || !bubble) {
        if (!shown.el.isConnected) bridgeRef.current.dismissActive();
        return;
      }
      const bubbleRect = bubble.getBoundingClientRect();
      const next = resolveFloatingPopoverPosition({
        anchorRect: shown.el.getBoundingClientRect(),
        popoverSize: { width: bubbleRect.width, height: bubbleRect.height },
        viewport: { width: window.innerWidth, height: window.innerHeight },
        placement,
        offset: OFFSET,
        margin: MARGIN,
      });
      setPos({ left: next.left, top: next.top });
    };
    update();
    window.addEventListener("resize", update);
    // scroll/resize 없이 anchor가 제거·이동되는 경우(표 정렬·필터·페이지네이션·비동기 갱신)를 감지 —
    // detached면 update()가 닫는다(pointerout은 제거된 노드에서 신뢰성 없음). 툴팁이 떠 있는
    // 동안만 도는 저빈도(200ms) 폴링이라 비용 미미.
    const poll = window.setInterval(update, 200);
    return () => {
      window.removeEventListener("resize", update);
      window.clearInterval(poll);
    };
  }, [shown, portalEl, placement]);

  // Tooltip의 Escape dismiss (WCAG 1.4.13). effect의 focus/pointer 상태도 함께 갱신해 같은
  // focus 수명 안에서 pointer 경계가 닫힌 버블을 되살리지 않게 한다. 흡수(stopPropagation)하지
  // 않아 상위 Escape를 가로채지 않는다.
  useEffect(() => {
    if (!shown) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") bridgeRef.current.dismissActive();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [shown]);

  if (!shown || !portalEl) return null;

  const dismissIfBlockingPointerTarget = (event: ReactMouseEvent<HTMLSpanElement>) => {
    const bubble = bubbleRef.current;
    if (
      !bubble ||
      !isTooltipBubbleBlockingPointerTarget({
        bubble,
        trigger: shown.el,
        clientX: event.clientX,
        clientY: event.clientY,
      })
    ) {
      return false;
    }

    bridgeRef.current.dismissCoveredTarget();
    return true;
  };

  // aria-hidden — 잘린 텍스트도 DOM엔 전문이 있어 스크린리더는 이미 읽는다(시각 보강 전용, 중복 안내 회피).
  return createPortal(
    <Bubble
      ref={bubbleRef}
      role="tooltip"
      aria-hidden="true"
      style={{ left: pos.left, top: pos.top }}
      onMouseEnter={(event) => {
        if (!dismissIfBlockingPointerTarget(event)) bridgeRef.current.enterBubble();
      }}
      onMouseMove={(event) => {
        if (!dismissIfBlockingPointerTarget(event)) bridgeRef.current.enterBubble();
      }}
      onMouseLeave={() => bridgeRef.current.leaveBubble()}
    >
      {shown.text}
    </Bubble>,
    portalEl,
  );
}

TruncationTooltipHost.displayName = "TruncationTooltipHost";
